import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { type AgentRow, type ProvisionAgentResponse, api } from "../lib/api";

type AgentsResp = { items: AgentRow[] };

export function AgentsList() {
	const qc = useQueryClient();
	const { data, isLoading, error } = useQuery<AgentsResp>({
		queryKey: ["agents"],
		queryFn: () => api.get("/agents"),
	});

	const [oneShotSecret, setOneShotSecret] = useState<ProvisionAgentResponse | null>(null);
	const [displayName, setDisplayName] = useState("Org agent");

	const provisionMut = useMutation({
		mutationFn: (name: string) =>
			api.post<ProvisionAgentResponse>("/agents/provision", { displayName: name }),
		onSuccess: (resp) => {
			setOneShotSecret(resp);
			qc.invalidateQueries({ queryKey: ["agents"] });
		},
	});

	const pauseMut = useMutation({
		mutationFn: (id: string) => api.post(`/agents/${id}/pause`, { reason: "manual" }),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["agents"] }),
	});

	const resumeMut = useMutation({
		mutationFn: (id: string) => api.post(`/agents/${id}/resume`, {}),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["agents"] }),
	});

	if (isLoading) return <div className="p-6">Loading…</div>;
	if (error) return <div className="p-6 text-red-700">Error: {(error as Error).message}</div>;
	const items = data?.items ?? [];
	const hasActiveAgent = items.some((a) => a.status === "active");

	return (
		<div className="p-6">
			<div className="mb-4 flex items-center justify-between">
				<h1 className="text-xl font-semibold">Agents</h1>
				{!hasActiveAgent && (
					<form
						className="flex gap-2"
						onSubmit={(e) => {
							e.preventDefault();
							provisionMut.mutate(displayName);
						}}
					>
						<input
							value={displayName}
							onChange={(e) => setDisplayName(e.target.value)}
							className="rounded border border-neutral-300 px-2 py-1 text-sm"
							placeholder="Agent display name"
						/>
						<button
							type="submit"
							disabled={provisionMut.isPending || displayName.trim().length === 0}
							className="rounded bg-emerald-600 px-3 py-1 text-sm text-white hover:bg-emerald-700 disabled:opacity-50"
						>
							{provisionMut.isPending ? "Provisioning…" : "Provision agent"}
						</button>
					</form>
				)}
			</div>

			{provisionMut.error && (
				<div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
					{String((provisionMut.error as Error).message)}
				</div>
			)}

			<table className="w-full text-sm">
				<thead className="text-left text-neutral-500">
					<tr>
						<th className="py-1">Display name</th>
						<th>OIDC client</th>
						<th>Status</th>
						<th>Created</th>
						<th />
					</tr>
				</thead>
				<tbody>
					{items.map((a) => (
						<tr key={a.id} className="border-t border-neutral-200">
							<td className="py-1">{a.displayName}</td>
							<td className="font-mono text-xs">{a.oidcClientId}</td>
							<td>
								<span
									className={
										a.status === "active"
											? "rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800"
											: "rounded bg-neutral-100 px-2 py-0.5 text-xs text-neutral-800"
									}
								>
									{a.status}
								</span>
								{a.pausedReason && (
									<span className="ml-2 text-xs text-neutral-500">({a.pausedReason})</span>
								)}
							</td>
							<td className="text-xs text-neutral-500">
								{new Date(a.createdAt * 1000).toISOString().slice(0, 19)}
							</td>
							<td className="text-right">
								{a.status === "active" ? (
									<button
										type="button"
										onClick={() => pauseMut.mutate(a.id)}
										className="rounded border border-neutral-300 px-2 py-1 text-xs hover:bg-neutral-50"
									>
										Pause
									</button>
								) : (
									<button
										type="button"
										onClick={() => resumeMut.mutate(a.id)}
										className="rounded border border-neutral-300 px-2 py-1 text-xs hover:bg-neutral-50"
									>
										Resume
									</button>
								)}
							</td>
						</tr>
					))}
					{items.length === 0 && (
						<tr>
							<td colSpan={5} className="py-4 text-center text-neutral-500">
								No agent yet. Provision one to start AI-driven negotiations.
							</td>
						</tr>
					)}
				</tbody>
			</table>

			{oneShotSecret && <SecretModal resp={oneShotSecret} onClose={() => setOneShotSecret(null)} />}
		</div>
	);
}

function SecretModal({
	resp,
	onClose,
}: {
	resp: ProvisionAgentResponse;
	onClose: () => void;
}) {
	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
			<div className="w-full max-w-lg rounded bg-white p-6 shadow-lg">
				<h2 className="mb-2 text-lg font-semibold">Agent provisioned</h2>
				<p className="mb-4 text-sm text-neutral-700">
					Save the client secret below — it is only shown once. The agent's identity is bound to
					this OIDC client and will run inside the manager-in-the-loop FSM.
				</p>
				<dl className="mb-4 space-y-2 text-sm">
					<div>
						<dt className="text-xs text-neutral-500">OIDC client id</dt>
						<dd className="font-mono">{resp.agent.oidcClientId}</dd>
					</div>
					<div>
						<dt className="text-xs text-neutral-500">OIDC client secret (one-time)</dt>
						<dd className="break-all rounded bg-amber-50 p-2 font-mono text-xs">
							{resp.oidcClientSecret ?? "(not returned — client already existed on auth platform)"}
						</dd>
					</div>
					<div>
						<dt className="text-xs text-neutral-500">Notice</dt>
						<dd className="text-xs text-neutral-700">{resp.notice}</dd>
					</div>
				</dl>
				<div className="flex justify-end">
					<button
						type="button"
						onClick={onClose}
						className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
					>
						I have saved the secret
					</button>
				</div>
			</div>
		</div>
	);
}
